import create from "zustand";
import { configurePersist } from "zustand-persist";

const { persist: _persist } = configurePersist({
  rootKey: "root",
  storage: sessionStorage,
});

const persist = _persist as any;

export enum AllocationMatrixStatus {
  DRAFT = "DRAFT",
  ACTIVE = "ACTIVE",
  SUPERSEDED = "SUPERSEDED",
  ARCHIVED = "ARCHIVED",
}

export enum NoMatchBehaviour {
  BLOCK = "BLOCK",
  FALLBACK_RULE = "FALLBACK_RULE",
  MANUAL_OVERRIDE = "MANUAL_OVERRIDE",
}

export enum RuleScope {
  PER_GRANT = "PER_GRANT",
  PER_EMPLOYEE = "PER_EMPLOYEE",
}

export enum QuantumMode {
  MIN = "MIN",
  MAX = "MAX",
  RANGE = "RANGE",
}

export enum AllocationMode {
  FIXED_OPTIONS = "FIXED_OPTIONS",
  ABSOLUTE_VALUE = "ABSOLUTE_VALUE",
}

export enum CostOrGain {
  COST = "COST",
  GAIN = "GAIN",
}

export enum FieldDataType {
  ENUM = "ENUM",
  DROPDOWN = "DROPDOWN",
  STRING = "STRING",
  BOOLEAN = "BOOLEAN",
  NUMBER = "NUMBER",
  DATE = "DATE",
  NUMERIC_RANGE = "NUMERIC_RANGE",
  DATE_RANGE = "DATE_RANGE",
}

export enum FieldStatus {
  ACTIVE = "ACTIVE",
  DEPRECATED = "DEPRECATED",
}

export interface AllocationFactor {
  name: string;
  fieldPath: string;
  dataType: FieldDataType;
  valueSet: string[] | null;
  isCustom: boolean;
  status: FieldStatus;
}

export interface Rule {
  priority: number;
  scope: RuleScope;
  allocationMode: AllocationMode;
  quantumMode: QuantumMode;
  quantumMin: number | null;
  quantumRecommended: number | null;
  quantumMax: number | null;
  costOrGain: CostOrGain | null;
  conditions: Record<string, string[]>;
}

export interface AllocationMatrix {
  name: string;
  description: string;
  version: number;
  isDefault: boolean;
  status: AllocationMatrixStatus;
  noMatchBehaviour: NoMatchBehaviour;
  selectedFactors: AllocationFactor[];
  rules: Rule[];
}

type AllocationMatrixStoreState = {
  matrixId: string | undefined;
  setMatrixId: (id: string | undefined) => void;
  isReadOnly: boolean;
  setIsReadOnly: (readOnly: boolean) => void;
  name: string;
  setName: (name: string) => void;
  description: string;
  setDescription: (description: string) => void;
  version: number;
  setVersion: (version: number) => void;
  isDefault: boolean;
  setIsDefault: (isDefault: boolean) => void;
  status: AllocationMatrixStatus;
  setStatus: (status: AllocationMatrixStatus) => void;
  noMatchBehaviour: NoMatchBehaviour;
  setNoMatchBehaviour: (behaviour: NoMatchBehaviour) => void;

  rules: Rule[];
  setRules: (rules: Rule[]) => void;
  addRule: (rule: Rule) => void;
  updateRule: (index: number, rule: Rule) => void;
  removeRule: (index: number) => void;
  reorderRules: (rules: Rule[]) => void;

  selectedFactors: AllocationFactor[];
  setSelectedFactors: (factors: AllocationFactor[]) => void;
  addFactor: (factor: AllocationFactor) => void;
  removeFactor: (fieldPath: string) => void;

  /** Persists the in-progress custom factor name across dialog open/close cycles */
  pendingFactorName: string;
  setPendingFactorName: (name: string) => void;

  getMatrix: () => AllocationMatrix;
  clear: () => void;
};

const initialState = {
  matrixId: undefined as string | undefined,
  isReadOnly: false,
  name: "",
  description: "",
  version: 1,
  isDefault: false,
  status: AllocationMatrixStatus.DRAFT,
  noMatchBehaviour: NoMatchBehaviour.BLOCK,
  rules: [] as Rule[],
  selectedFactors: [] as AllocationFactor[],
  pendingFactorName: "",
};

export const useAllocationMatrixStore = create<AllocationMatrixStoreState>(
  persist(
    {
      key: "allocationMatrix",
      name: "allocationMatrix",
      storage: sessionStorage,
    },
    (set: any, get: any) => ({
      ...initialState,

      setMatrixId: (matrixId: string | undefined) => set({ matrixId }),
      setIsReadOnly: (isReadOnly: boolean) => set({ isReadOnly }),
      setName: (name: string) => set({ name }),
      setDescription: (description: string) => set({ description }),
      setVersion: (version: number) => set({ version }),
      setIsDefault: (isDefault: boolean) => set({ isDefault }),
      setStatus: (status: AllocationMatrixStatus) => set({ status }),
      setNoMatchBehaviour: (noMatchBehaviour: NoMatchBehaviour) =>
        set({ noMatchBehaviour }),

      setRules: (rules: Rule[]) => set({ rules }),
      addRule: (rule: Rule) =>
        set((s: AllocationMatrixStoreState) => ({
          rules: [...s.rules, rule],
        })),
      updateRule: (index: number, rule: Rule) =>
        set((s: AllocationMatrixStoreState) => ({
          rules: s.rules.map((r, i) => (i === index ? rule : r)),
        })),
      removeRule: (index: number) =>
        set((s: AllocationMatrixStoreState) => ({
          rules: s.rules.filter((_, i) => i !== index),
        })),
      reorderRules: (rules: Rule[]) => set({ rules }),

      setSelectedFactors: (selectedFactors: AllocationFactor[]) =>
        set({ selectedFactors }),
      addFactor: (factor: AllocationFactor) =>
        set((s: AllocationMatrixStoreState) => {
          if (
            !factor?.fieldPath ||
            s.selectedFactors.some((f) => f.fieldPath === factor.fieldPath)
          ) {
            return {};
          }
          return { selectedFactors: [...s.selectedFactors, factor] };
        }),
      removeFactor: (fieldPath: string) =>
        set((s: AllocationMatrixStoreState) => ({
          selectedFactors: s.selectedFactors.filter(
            (f) => f.fieldPath !== fieldPath
          ),
        })),

      setPendingFactorName: (pendingFactorName: string) =>
        set({ pendingFactorName }),

      getMatrix: (): AllocationMatrix => {
        const s = get() as AllocationMatrixStoreState;
        return {
          name: s.name,
          description: s.description,
          version: s.version,
          isDefault: s.isDefault,
          status: s.status,
          noMatchBehaviour: s.noMatchBehaviour,
          selectedFactors: s.selectedFactors,
          rules: s.rules,
        };
      },

      clear: () => set(initialState),
    })
  )
);
